import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead, sqlEscape } from "../utils/sqlClient";
import { estimatorColor } from "../config/estimatorColors";
import { useIsAdmin } from "../auth/AdminContext";
import { syncDashboardStatuses, previewReactivations, setEstimateSyncLock } from "../utils/hubspotSync";
import { SALES_DEVELOPMENT_STATUSES, REALIGNMENT_STATUSES, PROPOSAL_ISSUED_STATUSES, ACTIVE_PIPELINE_STATUSES, ALL_PIPELINE_STATUSES } from "../config/dashboardStatuses";
import { deriveOverrideState } from "../utils/syncOverrideState";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtCurrency(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return "—";
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function fmtDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "2-digit" });
}

function toInputDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return "";
  return d.toISOString().slice(0, 10);
}

function sevenDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

// ── Section order (drag to rearrange) ───────────────────────────────────────
// Per-viewer preference, not a shared DashboardConfig row — unlike the
// CleanShield filter this isn't a company policy, it's "what do I want to
// see first," so it's stored in localStorage (not synced across devices) and
// never goes through the admin-gated sp_SaveDashboardConfig path.
const SECTION_ORDER_STORAGE_KEY = "execDashboardSectionOrder";
const DEFAULT_SECTION_ORDER = ["active", "realignment", "proposalIssued", "salesDevelopment", "sent"];
const SECTION_LABELS = {
  active: "Active Estimating",
  realignment: "Post-Issuance Realignment",
  proposalIssued: "Proposal Issued",
  salesDevelopment: "Future Pipeline",
  sent: "Sent Since Last Update",
};

function loadSectionOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(SECTION_ORDER_STORAGE_KEY));
    if (Array.isArray(saved) && saved.length === DEFAULT_SECTION_ORDER.length
      && DEFAULT_SECTION_ORDER.every(k => saved.includes(k))) {
      return saved;
    }
  } catch {}
  return DEFAULT_SECTION_ORDER;
}

// Collapsed by default: Proposal Issued (2026-09-21, Ben — "own category
// and/or hidden") — the estimating work is effectively done on these, so
// they're out of the way until wanted, not deleted from view. Every section
// is collapsible via the same toggle; this is just the starting state.
const COLLAPSED_SECTIONS_STORAGE_KEY = "execDashboardCollapsedSections";
const DEFAULT_COLLAPSED_SECTIONS = { proposalIssued: true };

function loadCollapsedSections() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_STORAGE_KEY));
    if (saved && typeof saved === "object") return saved;
  } catch {}
  return DEFAULT_COLLAPSED_SECTIONS;
}

// Splits dashboard rows into the three funnel-stage-specific buckets plus
// "remaining" (Active Estimating only, exclusively — see the 2026-09-21
// regrouping comment in src/config/dashboardStatuses.js) — pulled out as a
// pure function so the split (each row lands in exactly one bucket) is
// unit-testable without rendering the page.
export function bucketDashboardRows(rows) {
  const salesDevelopment = rows.filter(r => SALES_DEVELOPMENT_STATUSES.includes(r.Status));
  const realignment = rows.filter(r => REALIGNMENT_STATUSES.includes(r.Status));
  const proposalIssued = rows.filter(r => PROPOSAL_ISSUED_STATUSES.includes(r.Status));
  const remaining = rows.filter(r =>
    !SALES_DEVELOPMENT_STATUSES.includes(r.Status) && !REALIGNMENT_STATUSES.includes(r.Status)
      && !PROPOSAL_ISSUED_STATUSES.includes(r.Status)
  );
  return { salesDevelopment, realignment, proposalIssued, remaining };
}

function revisionLabel(row) {
  if (!row.LatestRevisionNumber) return "—";
  return row.LatestRevisionNumber === 1 ? "Initial" : `Rev ${row.LatestRevisionNumber - 1}`;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function DueDateCell({ dateStr, isOverride }) {
  const days = daysUntil(dateStr);
  if (!dateStr) return <span style={{ color: "var(--text3)" }}>—</span>;
  let color = "inherit";
  let weight = 400;
  if (days !== null) {
    if (days < 0) color = "#991b1b";
    else if (days <= 3) { color = "#c2410c"; weight = 600; }
    else if (days <= 7) { color = "#d97706"; weight = 500; }
  }
  return (
    <span style={{ color, fontWeight: weight, display: "inline-flex", alignItems: "center", gap: 3 }}>
      {fmtDate(dateStr)}
      {isOverride && <span title="Manually overridden" style={{ fontSize: 10, opacity: 0.7 }}>✎</span>}
    </span>
  );
}

// ── Inline cell editor ────────────────────────────────────────────────────────

function EditableDate({ value, isOverride, onSave, placeholder = "Set date" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); if (draft !== toInputDate(value)) onSave(draft || null, true); }}
        onKeyDown={e => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") { setEditing(false); }
        }}
        style={{ fontSize: 12, padding: "1px 4px", border: "1px solid var(--cs-border)", borderRadius: 3 }}
      />
    );
  }
  return (
    <span
      onClick={() => { setDraft(toInputDate(value)); setEditing(true); }}
      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3 }}
      title="Click to edit"
    >
      {value
        ? <><DueDateCell dateStr={value} isOverride={isOverride} /></>
        : <span style={{ color: "var(--text3)", fontSize: 11 }}>{placeholder}</span>}
    </span>
  );
}

function EditableText({ value, onSave, placeholder = "Add notes" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); onSave(draft); }}
        onKeyDown={e => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") setEditing(false);
        }}
        style={{ width: "100%", fontSize: 12, padding: "1px 4px", border: "1px solid var(--cs-border)", borderRadius: 3 }}
      />
    );
  }
  return (
    <span
      onClick={() => { setDraft(value || ""); setEditing(true); }}
      style={{ cursor: "pointer", display: "block", minWidth: 60 }}
      title="Click to edit"
    >
      {value || <span style={{ color: "var(--text3)", fontSize: 11 }}>{placeholder}</span>}
    </span>
  );
}

function EditableCurrency({ value, onSave, placeholder = "—" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); onSave(draft === "" ? null : Number(draft)); }}
        onKeyDown={e => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") setEditing(false);
        }}
        style={{ width: 100, fontSize: 12, padding: "1px 4px", border: "1px solid var(--cs-border)", borderRadius: 3 }}
      />
    );
  }
  return (
    <span
      onClick={() => { setDraft(value != null ? String(value) : ""); setEditing(true); }}
      style={{ cursor: "pointer" }}
      title="Click to edit"
    >
      {value != null ? fmtCurrency(value) : <span style={{ color: "var(--text3)", fontSize: 11 }}>{placeholder}</span>}
    </span>
  );
}

function EditableMargin({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        step="0.1"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); onSave(draft === "" ? null : Number(draft)); }}
        onKeyDown={e => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") setEditing(false);
        }}
        style={{ width: 60, fontSize: 12, padding: "1px 4px", border: "1px solid var(--cs-border)", borderRadius: 3 }}
      />
    );
  }
  return (
    <span
      onClick={() => { setDraft(value != null ? String(value) : ""); setEditing(true); }}
      style={{ cursor: "pointer" }}
      title="Click to edit"
    >
      {value != null ? `${value}%` : <span style={{ color: "var(--text3)", fontSize: 11 }}>—</span>}
    </span>
  );
}

function SourceToggle({ value, onSave }) {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {["sales", "construction"].map(s => (
        <button
          key={s}
          onClick={() => onSave(value === s ? null : s)}
          style={{
            padding: "1px 7px", fontSize: 10, borderRadius: 3, border: "1px solid var(--cs-border)",
            background: value === s ? (s === "sales" ? "#dbeafe" : "#fef9c3") : "transparent",
            color: value === s ? (s === "sales" ? "#1d4ed8" : "#713f12") : "var(--text3)",
            cursor: "pointer", fontWeight: value === s ? 600 : 400,
          }}
        >
          {s === "sales" ? "Sales" : "Const."}
        </button>
      ))}
    </span>
  );
}

// ── Section table ─────────────────────────────────────────────────────────────

const TH = ({ children, style }) => (
  <th style={{
    padding: "4px 8px", textAlign: "left", fontSize: 11, fontWeight: 600,
    color: "var(--text2)", borderBottom: "1px solid var(--cs-border)",
    whiteSpace: "nowrap", background: "#f8f9fb", ...style,
  }}>
    {children}
  </th>
);

const TD = ({ children, style }) => (
  <td style={{ padding: "5px 8px", fontSize: 12, verticalAlign: "middle", ...style }}>
    {children}
  </td>
);

function SectionTable({ title, rows, isSent, totalLabel, onSave, onHide, onLock, onPullForward }) {
  const total = rows.reduce((sum, r) => {
    const v = isSent ? r.ActualShippedValue : r.EstimatedValue;
    return sum + (Number(v) || 0);
  }, 0);

  if (!isSent) {
    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{
          padding: "5px 12px", background: "#1e293b", color: "#fff",
          fontSize: 13, fontWeight: 600, borderRadius: "4px 4px 0 0",
        }}>
          {title}
          <span style={{ float: "right", fontWeight: 400, fontSize: 12, opacity: 0.8 }}>
            {rows.length} estimate{rows.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            <thead>
              <tr>
                <TH>Description</TH>
                <TH>Project Type</TH>
                <TH>Estimator</TH>
                <TH>Sales Person</TH>
                <TH>Est. Value</TH>
                <TH>Revision</TH>
                <TH>Due Date</TH>
                <TH>Active Since</TH>
                <TH>RFI Due</TH>
                <TH>Job Walk</TH>
                <TH style={{ minWidth: 60 }}>Source</TH>
                <TH style={{ minWidth: 120 }}>Comments</TH>
                <TH>Area (SF)</TH>
                {(onHide || onLock || onPullForward) && <TH style={{ minWidth: 108 }} />}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const { bg, text } = estimatorColor(r.AssignedEstimator);
                return (
                  <tr key={r.ProjectID} style={{ background: bg, color: text, borderBottom: "1px solid rgba(0,0,0,0.07)" }}>
                    <TD>
                      <a href={`/estimates/${r.ProjectID}`} style={{ color: "inherit", fontWeight: 500, textDecoration: "none" }}>
                        {r.ProjectName}
                      </a>
                      {r.ClientName && <div style={{ fontSize: 10, opacity: 0.7 }}>{r.ClientName}</div>}
                      {r.SyncLocked && (
                        <div style={{ fontSize: 10, color: "#7c2d12", marginTop: 2 }} title={r.SyncLockReason}>
                          🔒 HubSpot sync locked
                        </div>
                      )}
                    </TD>
                    <TD>{r.ProjectType || "—"}</TD>
                    <TD style={{ whiteSpace: "nowrap" }}>{r.AssignedEstimator || "Unassigned"}</TD>
                    <TD style={{ whiteSpace: "nowrap" }}>{r.DealOwner || "—"}</TD>
                    <TD style={{ whiteSpace: "nowrap" }}>
                      <EditableCurrency
                        value={r.EstimatedValue}
                        onSave={v => onSave(r.ProjectID, { estimated_value: v })}
                        placeholder="Set value"
                      />
                      {r.HubSpotAmount != null && (
                        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}
                          title="Deal amount from HubSpot">
                          HS: {fmtCurrency(r.HubSpotAmount)}
                        </div>
                      )}
                    </TD>
                    <TD>{revisionLabel(r)}</TD>
                    <TD>
                      <EditableDate
                        value={r.EffectiveProposalDueDate}
                        isOverride={!!r.ProposalDueDateIsOverride}
                        onSave={(d, isOverride) => onSave(r.ProjectID, {
                          proposal_due_date: d,
                          proposal_due_date_is_override: isOverride ? 1 : 0,
                        })}
                        placeholder="Set due date"
                      />
                    </TD>
                    <TD>
                      <EditableDate
                        value={r.EffectiveActiveEstimatingDate}
                        isOverride={!!r.ActiveEstimatingIsOverride}
                        onSave={(d, isOverride) => onSave(r.ProjectID, {
                          active_estimating_date: d,
                          active_estimating_is_override: isOverride ? 1 : 0,
                        })}
                        placeholder="Set date"
                      />
                    </TD>
                    <TD>
                      <EditableDate
                        value={r.RFIDueDate}
                        isOverride={false}
                        onSave={d => onSave(r.ProjectID, { rfi_due_date: d })}
                        placeholder="Set RFI"
                      />
                    </TD>
                    <TD>
                      <EditableDate
                        value={r.JobWalkDate}
                        isOverride={false}
                        onSave={d => onSave(r.ProjectID, { job_walk_date: d })}
                        placeholder="Set walk"
                      />
                    </TD>
                    <TD>
                      <SourceToggle
                        value={r.Source}
                        onSave={s => onSave(r.ProjectID, { source: s })}
                      />
                    </TD>
                    <TD>
                      <EditableText
                        value={r.DashboardComments}
                        onSave={v => onSave(r.ProjectID, { comments: v })}
                        placeholder="Notes"
                      />
                    </TD>
                    <TD style={{ whiteSpace: "nowrap" }}>
                      {r.ProjectSF ? Number(r.ProjectSF).toLocaleString() : "—"}
                    </TD>
                    {(onHide || onLock || onPullForward) && (
                      <TD>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {onPullForward && (
                            <button
                              onClick={() => onPullForward(r)}
                              title="Pull into Active Estimating — start counting this as active work now, ahead of HubSpot's own stage"
                              style={{
                                padding: "2px 7px", fontSize: 11, border: "1px solid #93c5fd",
                                borderRadius: 3, background: "#eff6ff", color: "#1d4ed8",
                                cursor: "pointer", opacity: 0.8,
                              }}
                            >
                              ▶ Active
                            </button>
                          )}
                          {onHide && (
                            <button
                              onClick={() => onHide(r.ProjectID)}
                              title="Hide this deal from dashboard (marks it inactive)"
                              style={{
                                padding: "2px 7px", fontSize: 11, border: "1px solid #fca5a5",
                                borderRadius: 3, background: "#fee2e2", color: "#991b1b",
                                cursor: "pointer", opacity: 0.7,
                              }}
                            >
                              Hide
                            </button>
                          )}
                          {onLock && (
                            <button
                              onClick={() => onLock(r)}
                              title="Override — freeze this against the HubSpot sync (HubSpot is wrong for this deal)"
                              style={{
                                padding: "2px 7px", fontSize: 11, border: "1px solid #fdba74",
                                borderRadius: 3, background: "#fff7ed", color: "#7c2d12",
                                cursor: "pointer", opacity: 0.7,
                              }}
                            >
                              🔒
                            </button>
                          )}
                        </div>
                      </TD>
                    )}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={(onHide || onLock || onPullForward) ? 14 : 13} style={{ padding: "12px 8px", fontSize: 12, color: "var(--text3)", textAlign: "center" }}>
                    No active estimates in this category
                  </td>
                </tr>
              )}
            </tbody>
            {total > 0 && (
              <tfoot>
                <tr style={{ background: "#f1f5f9", borderTop: "2px solid #cbd5e1" }}>
                  <td colSpan={4} style={{ padding: "5px 8px", fontSize: 12, fontWeight: 600 }}>
                    {totalLabel || "Total in House"}
                  </td>
                  <td style={{ padding: "5px 8px", fontSize: 12, fontWeight: 600 }}>
                    {fmtCurrency(total)}
                  </td>
                  <td colSpan={8} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  }

  // ── Sent Since Last Update layout ────────────────────────────────────────
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        padding: "5px 12px", background: "#14532d", color: "#fff",
        fontSize: 13, fontWeight: 600, borderRadius: "4px 4px 0 0",
      }}>
        {title}
        <span style={{ float: "right", fontWeight: 400, fontSize: 12, opacity: 0.8 }}>
          {rows.length} proposal{rows.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
          <thead>
            <tr>
              <TH>Description</TH>
              <TH>Project Type</TH>
              <TH>Estimator</TH>
              <TH>Sales Person</TH>
              <TH>Actual Value</TH>
              <TH>Revision</TH>
              <TH>Shipped</TH>
              <TH>Margin</TH>
              <TH style={{ minWidth: 120 }}>Comments</TH>
              <TH>Area (SF)</TH>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const { bg, text } = estimatorColor(r.AssignedEstimator);
              return (
                <tr key={r.ProjectID} style={{ background: bg, color: text, borderBottom: "1px solid rgba(0,0,0,0.07)" }}>
                  <TD>
                    <a href={`/estimates/${r.ProjectID}`} style={{ color: "inherit", fontWeight: 500, textDecoration: "none" }}>
                      {r.ProjectName}
                    </a>
                    {r.ClientName && <div style={{ fontSize: 10, opacity: 0.7 }}>{r.ClientName}</div>}
                  </TD>
                  <TD>{r.ProjectType || "—"}</TD>
                  <TD style={{ whiteSpace: "nowrap" }}>{r.AssignedEstimator || "Unassigned"}</TD>
                  <TD style={{ whiteSpace: "nowrap" }}>{r.DealOwner || "—"}</TD>
                  <TD style={{ whiteSpace: "nowrap" }}>
                    <EditableCurrency
                      value={r.ActualShippedValue}
                      onSave={v => onSave(r.ProjectID, { actual_shipped_value: v })}
                      placeholder="Set value"
                    />
                  </TD>
                  <TD>{revisionLabel(r)}</TD>
                  <TD>
                    <EditableDate
                      value={r.ProposalShippedDate}
                      isOverride={false}
                      onSave={d => onSave(r.ProjectID, { proposal_shipped_date: d })}
                    />
                  </TD>
                  <TD>
                    <EditableMargin
                      value={r.MarginPct}
                      onSave={v => onSave(r.ProjectID, { margin_pct: v })}
                    />
                  </TD>
                  <TD>
                    <EditableText
                      value={r.DashboardComments}
                      onSave={v => onSave(r.ProjectID, { comments: v })}
                      placeholder="Notes"
                    />
                  </TD>
                  <TD style={{ whiteSpace: "nowrap" }}>
                    {r.ProjectSF ? Number(r.ProjectSF).toLocaleString() : "—"}
                  </TD>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: "12px 8px", fontSize: 12, color: "var(--text3)", textAlign: "center" }}>
                  No proposals shipped in this period
                </td>
              </tr>
            )}
          </tbody>
          {total > 0 && (
            <tfoot>
              <tr style={{ background: "#f0fdf4", borderTop: "2px solid #86efac" }}>
                <td colSpan={4} style={{ padding: "5px 8px", fontSize: 12, fontWeight: 600 }}>
                  Total Sent
                </td>
                <td style={{ padding: "5px 8px", fontSize: 12, fontWeight: 600 }}>
                  {fmtCurrency(total)}
                </td>
                <td colSpan={5} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Mark as Shipped overlay ───────────────────────────────────────────────────

function MarkShippedDialog({ row, onSave, onClose }) {
  const [shippedDate, setShippedDate] = useState(toInputDate(new Date()));
  const [actualValue, setActualValue] = useState(row.ActualShippedValue ?? row.EstimatedValue ?? "");
  const [margin, setMargin] = useState(row.MarginPct ?? "");

  function handleSave() {
    if (!shippedDate) return;
    onSave(row.ProjectID, {
      proposal_shipped_date: shippedDate,
      actual_shipped_value: actualValue === "" ? null : Number(actualValue),
      margin_pct: margin === "" ? null : Number(margin),
    });
    onClose();
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 8, padding: 24, width: 340,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
          Mark Proposal Shipped
        </div>
        <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16 }}>
          {row.ProjectName}
        </div>
        <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
          Shipped Date *
        </label>
        <input type="date" value={shippedDate} onChange={e => setShippedDate(e.target.value)}
          style={{ width: "100%", padding: "5px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 4, marginBottom: 12, boxSizing: "border-box" }} />
        <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
          Actual Value
        </label>
        <input type="number" value={actualValue} onChange={e => setActualValue(e.target.value)}
          placeholder="e.g. 2500000"
          style={{ width: "100%", padding: "5px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 4, marginBottom: 12, boxSizing: "border-box" }} />
        <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
          Margin %
        </label>
        <input type="number" step="0.1" value={margin} onChange={e => setMargin(e.target.value)}
          placeholder="e.g. 18.5"
          style={{ width: "100%", padding: "5px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 4, marginBottom: 20, boxSizing: "border-box" }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "6px 16px", fontSize: 13, border: "1px solid var(--cs-border)",
            borderRadius: 4, background: "none", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={!shippedDate} style={{
            padding: "6px 16px", fontSize: 13, border: "none",
            borderRadius: 4, background: "#14532d", color: "#fff", cursor: "pointer",
            opacity: !shippedDate ? 0.5 : 1,
          }}>Mark Shipped</button>
        </div>
      </div>
    </div>
  );
}

// ── CleanShield filter edit panel (admin only) ────────────────────────────────

function DivisionFilterEditor({ owners, onSave, onClose, saving, allDealOwners }) {
  const [draft, setDraft] = useState([...owners]);
  const [newName, setNewName] = useState("");

  function addName(name) {
    const trimmed = (name || newName).trim();
    if (!trimmed || draft.some(n => n.toLowerCase() === trimmed.toLowerCase())) return;
    setDraft(prev => [...prev, trimmed]);
    setNewName("");
  }

  // Owners present in actual data but not yet in the filter list
  const suggestable = allDealOwners.filter(
    n => n && !draft.some(d => d.toLowerCase() === n.toLowerCase())
  );

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 8, padding: 24, width: 440,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
          CleanShield Division — Deal Owners
        </div>
        <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 16, lineHeight: 1.5 }}>
          Deals owned by these people are classified as CleanShield and hidden by default.
          The list persists for all users.
        </div>

        <div style={{ marginBottom: 12 }}>
          {draft.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text3)", padding: "8px 0" }}>
              No CleanShield owners configured — all deals will show.
            </div>
          )}
          {draft.map((name, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{
                flex: 1, fontSize: 13, padding: "5px 8px",
                background: "#fef3c7", borderRadius: 4, border: "1px solid #fde68a",
                color: "#78350f", fontWeight: 500,
              }}>
                {name}
              </span>
              <button
                onClick={() => setDraft(prev => prev.filter((_, j) => j !== i))}
                style={{
                  padding: "3px 10px", fontSize: 12, border: "1px solid #fca5a5",
                  borderRadius: 3, background: "#fee2e2", color: "#991b1b", cursor: "pointer",
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        {suggestable.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>
              Deal owners currently in the dashboard — click to add:
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {suggestable.map(name => (
                <button
                  key={name}
                  onClick={() => addName(name)}
                  style={{
                    padding: "3px 10px", fontSize: 12, border: "1px solid #93c5fd",
                    borderRadius: 12, background: "#eff6ff", color: "#1e40af",
                    cursor: "pointer", fontWeight: 500,
                  }}
                >
                  + {name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") addName(); }}
            placeholder="Or type a name manually"
            style={{ flex: 1, padding: "5px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 4 }}
          />
          <button
            onClick={() => addName()}
            disabled={!newName.trim()}
            style={{
              padding: "5px 14px", fontSize: 12, border: "none",
              borderRadius: 4, background: "#1e293b", color: "#fff", cursor: "pointer",
              opacity: !newName.trim() ? 0.4 : 1,
            }}
          >
            Add
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "6px 16px", fontSize: 13, border: "1px solid var(--cs-border)",
            borderRadius: 4, background: "none", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={() => onSave(draft)} disabled={saving} style={{
            padding: "6px 16px", fontSize: 13, border: "none",
            borderRadius: 4, background: "#1e293b", color: "#fff", cursor: "pointer",
            opacity: saving ? 0.6 : 1,
          }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── HubSpot sync override dialog ──────────────────────────────────────────────
// Locking freezes Status/IsActive against the HubSpot sync — for when
// HubSpot itself is stale for this deal and Sales hasn't corrected it yet.
// The status picker defaults to what's currently shown (HubSpot's live
// label when known, else the stored Status) but is editable — locking
// doesn't just freeze the stale value in place, it lets the estimator
// correct it first (e.g. HubSpot still says "Active Estimating" but the
// proposal shipped months ago — correct it to "Proposal Sent", then lock).
// A reason is required so every override is attributed and visible in the
// "HubSpot Overrides" section below, not a silent local edit.
function SyncLockDialog({ row, onSave, onClose, saving }) {
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState(row.liveLabel || row.Status || "Active Estimating");

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 8, padding: 24, width: 420,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
          🔒 Override HubSpot Sync
        </div>
        <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 12 }}>
          {row.ProjectName || row.dealName}
        </div>
        <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 14, lineHeight: 1.5, background: "#f8f9fb", padding: "8px 10px", borderRadius: 4 }}>
          HubSpot currently shows this as <strong>{row.liveLabel || row.Status}</strong>. Correct it below if
          that's stale, then lock — the correction is saved here and frozen regardless of what HubSpot says,
          until someone unlocks it. Nothing is changed in HubSpot itself; this shows up on the dashboard's
          HubSpot Overrides list so it's visible, not silent.
        </div>
        <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
          Correct status to
        </label>
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 4, marginBottom: 14, boxSizing: "border-box" }}
        >
          {ALL_PIPELINE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
          Why? (required)
        </label>
        <textarea
          autoFocus
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Sales hasn't moved this in HubSpot — proposal actually shipped in July"
          rows={3}
          style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 4, marginBottom: 16, boxSizing: "border-box", resize: "vertical" }}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "6px 16px", fontSize: 13, border: "1px solid var(--cs-border)",
            borderRadius: 4, background: "none", cursor: "pointer",
          }}>Cancel</button>
          <button
            onClick={() => onSave({ reason, status })}
            disabled={saving || !reason.trim()}
            style={{
              padding: "6px 16px", fontSize: 13, border: "none",
              borderRadius: 4, background: "#7c2d12", color: "#fff", cursor: "pointer",
              opacity: (saving || !reason.trim()) ? 0.5 : 1,
            }}
          >
            {saving ? "Locking…" : "🔒 Lock"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pull Forward dialog (Future Pipeline → Active Estimating) ────────────────
// Same underlying mechanism as SyncLockDialog (sp_SetEstimateSyncLock with a
// corrected status, frozen against the sync) but purpose-built for the
// opposite direction: this estimate still shows as Prospecting / Qualified
// to Pursue / Qualification In Progress in HubSpot, but real estimating work
// has already started — an informal go-ahead, early scoping, etc. — so pull
// it forward onto Active Estimating now rather than waiting for HubSpot's
// own stage to catch up. Nothing is written back to HubSpot; the override
// still shows in "HubSpot Overrides" below and is tracked
// stale/resolved/needs_review exactly like any other override (2026-09-30,
// Ben: "pull it forward... in a similar way that we override the items that
// are in active estimating that shouldn't be").
function PullForwardDialog({ row, onSave, onClose, saving }) {
  const [reason, setReason] = useState("");

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 8, padding: 24, width: 420,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
          ▶ Pull Into Active Estimating
        </div>
        <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 12 }}>
          {row.ProjectName}
        </div>
        <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 14, lineHeight: 1.5, background: "#f8f9fb", padding: "8px 10px", borderRadius: 4 }}>
          HubSpot currently shows this as <strong>{row.Status}</strong>. Pulling it forward moves it onto
          Active Estimating on this dashboard right now — nothing changes in HubSpot itself. It will show up
          in HubSpot Overrides below until HubSpot's own stage catches up (or you unlock it there).
        </div>
        <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
          Why? (required)
        </label>
        <textarea
          autoFocus
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Sales gave the go-ahead informally — starting estimating ahead of the HubSpot stage"
          rows={3}
          style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 4, marginBottom: 16, boxSizing: "border-box", resize: "vertical" }}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "6px 16px", fontSize: 13, border: "1px solid var(--cs-border)",
            borderRadius: 4, background: "none", cursor: "pointer",
          }}>Cancel</button>
          <button
            onClick={() => onSave({ reason })}
            disabled={saving || !reason.trim()}
            style={{
              padding: "6px 16px", fontSize: 13, border: "none",
              borderRadius: 4, background: "#1d4ed8", color: "#fff", cursor: "pointer",
              opacity: (saving || !reason.trim()) ? 0.5 : 1,
            }}
          >
            {saving ? "Pulling forward…" : "▶ Pull Forward"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ExecutiveDashboard() {
  const { accounts } = useMsal();
  const userName = accounts[0]?.name || "";
  const isAdmin = useIsAdmin();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [sinceDate, setSinceDate] = useState(sevenDaysAgo);
  const [shippingRow, setShippingRow] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null); // increments on each load to trigger HS sync

  // ── Division filter state ───────────────────────────────────────────────────
  const [cleanShieldOwners, setCleanShieldOwners] = useState(["Shawn Moline", "Michael Konczak"]);
  const [showCleanShield, setShowCleanShield] = useState(false); // default: CleanSpace only
  const [editingFilter, setEditingFilter] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // ── HubSpot sync overrides (2026-09-21) ─────────────────────────────────────
  const [lockedEstimates, setLockedEstimates] = useState([]);
  const [lockDialogRow, setLockDialogRow] = useState(null); // row being locked/unlocked, or null
  const [reactivationPreview, setReactivationPreview] = useState(null); // null | 'loading' | array
  const [savingLock, setSavingLock] = useState(null);
  const [pullForwardRow, setPullForwardRow] = useState(null); // Future Pipeline row being pulled into Active Estimating, or null

  // ── Section order (drag to rearrange, 2026-09-21) ───────────────────────────
  const [sectionOrder, setSectionOrder] = useState(loadSectionOrder);
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState(loadCollapsedSections);

  const moveSectionBefore = useCallback((draggedKey, targetKey) => {
    if (!draggedKey || draggedKey === targetKey) return;
    setSectionOrder(prev => {
      const next = prev.filter(k => k !== draggedKey);
      next.splice(next.indexOf(targetKey), 0, draggedKey);
      try { localStorage.setItem(SECTION_ORDER_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const toggleSectionCollapsed = useCallback((key) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLLAPSED_SECTIONS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, config, locked] = await Promise.all([
        sqlRead(`EXEC sp_GetExecutiveDashboard`),
        sqlRead(`EXEC sp_GetDashboardConfig`),
        sqlRead(`EXEC sp_GetSyncLockedEstimates`),
      ]);
      setRows(data);
      setLockedEstimates(locked || []);
      setLoadedAt(Date.now());
      const ownerRow = (config || []).find(r => r.ConfigKey === "cleanshield_owners");
      if (ownerRow?.ConfigValue) {
        try { setCleanShieldOwners(JSON.parse(ownerRow.ConfigValue)); } catch {}
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Background HubSpot sync — fires after each SQL load completes.
  // Batch-reads live deal statuses from HubSpot, deactivates closed/won/lost
  // deals that the webhook missed, and updates any changed statuses or amounts.
  // Runs silently; the user already sees SQL data; this refines it in the background.
  const syncRowsRef = useRef([]);
  syncRowsRef.current = rows;
  useEffect(() => {
    if (!loadedAt) return;
    const currentRows = syncRowsRef.current;
    let cancelled = false;
    syncDashboardStatuses(currentRows)
      .then(({ deactivated, updated }) => {
        if (cancelled) return;
        if (deactivated.size || updated.size) {
          setRows(prev =>
            prev
              .filter(r => !deactivated.has(r.ProjectID))
              .map(r => {
                const changes = updated.get(r.ProjectID);
                return changes ? { ...r, ...changes } : r;
              })
          );
        }
      })
      .catch(e => console.warn("Dashboard HubSpot sync failed:", e));
    return () => { cancelled = true; };
  }, [loadedAt]);

  const handleSave = useCallback(async (projectId, patch) => {
    setSaving(projectId);
    try {
      const payload = JSON.stringify({ project_id: projectId, ...patch, updated_by: userName });
      await sqlRead(`EXEC sp_SaveEstimateMilestone @payload='${sqlEscape(payload)}'`);
      setRows(prev => prev.map(r => {
        if (r.ProjectID !== projectId) return r;
        const updated = { ...r };
        if (patch.source !== undefined) updated.Source = patch.source;
        if (patch.comments !== undefined) updated.DashboardComments = patch.comments;
        if (patch.estimated_value !== undefined) updated.EstimatedValue = patch.estimated_value;
        if (patch.active_estimating_date !== undefined) {
          updated.ActiveEstimatingDate = patch.active_estimating_date;
          updated.EffectiveActiveEstimatingDate = patch.active_estimating_date || r.CreatedAt?.slice(0, 10);
          updated.ActiveEstimatingIsOverride = patch.active_estimating_is_override ?? 1;
        }
        if (patch.rfi_due_date !== undefined) updated.RFIDueDate = patch.rfi_due_date;
        if (patch.job_walk_date !== undefined) updated.JobWalkDate = patch.job_walk_date;
        if (patch.proposal_due_date !== undefined) {
          updated.ProposalDueDate = patch.proposal_due_date;
          updated.EffectiveProposalDueDate = patch.proposal_due_date || r.BidDueDate;
          updated.ProposalDueDateIsOverride = patch.proposal_due_date_is_override ?? 1;
        }
        if (patch.proposal_shipped_date !== undefined) updated.ProposalShippedDate = patch.proposal_shipped_date;
        if (patch.actual_shipped_value !== undefined) updated.ActualShippedValue = patch.actual_shipped_value;
        if (patch.margin_pct !== undefined) updated.MarginPct = patch.margin_pct;
        return updated;
      }));
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(null);
    }
  }, [userName]);

  const handleHide = useCallback(async (projectId) => {
    if (!window.confirm("Hide this deal from the dashboard? (Sets it inactive — it will no longer appear until re-activated via HubSpot.)")) return;
    setSaving(projectId);
    try {
      await sqlRead(`EXEC sp_DeactivateEstimate @project_id='${sqlEscape(String(projectId))}'`);
      setRows(prev => prev.filter(r => r.ProjectID !== projectId));
    } catch (e) {
      alert(`Hide failed: ${e.message}`);
    } finally {
      setSaving(null);
    }
  }, []);

  // ── HubSpot sync override (2026-09-21) ──────────────────────────────────────
  // Locking freezes Status/IsActive against every sync path (webhook, on-open
  // sync, nightly dashboard-reconcile) — for when HubSpot itself is stale for
  // a specific deal and Sales hasn't corrected it yet. Reloads the whole
  // dashboard afterward rather than hand-patching state in three places.
  //
  // status (optional, lock only): the corrected stage label from
  // SyncLockDialog's picker — written to Estimates.Status alongside the
  // lock. IsActive is derived from it (in one of the 10 "in-play" stages →
  // shown; a closed-type stage like Closed Lost/No Bid/On Hold → hidden) so
  // the estimator doesn't have to separately think about the Hide checkbox —
  // picking the right stage is enough. Unlock never touches Status.
  //
  // hubspotStatusAtLock (lock only): what HubSpot's live label actually
  // showed at this moment — separate from `status`, the estimator's
  // correction — so sp_SetEstimateSyncLock can snapshot it for the
  // stale/resolved/needs_review read (deriveOverrideState).
  const handleSetLock = useCallback(async ({ projectId, locked, reason, status, hubspotStatusAtLock }) => {
    setSavingLock(projectId);
    try {
      await setEstimateSyncLock({
        project_id: projectId,
        locked: locked ? 1 : 0,
        reason: locked ? reason : undefined,
        locked_by: userName,
        status: locked ? status : undefined,
        hubspot_status_at_lock: locked ? hubspotStatusAtLock : undefined,
        also_set_active: locked && status ? (ACTIVE_PIPELINE_STATUSES.includes(status) ? 1 : 0) : undefined,
      });
      setLockDialogRow(null);
      setReactivationPreview(prev => Array.isArray(prev) ? prev.filter(r => r.ProjectID !== projectId) : prev);
      await load();
    } catch (e) {
      alert(`${locked ? "Lock" : "Unlock"} failed: ${e.message}`);
    } finally {
      setSavingLock(null);
    }
  }, [userName, load]);

  // ── Pull Forward (Future Pipeline → Active Estimating, 2026-09-30) ──────────
  // Same sp_SetEstimateSyncLock call handleSetLock makes, fixed to
  // status="Active Estimating" and driven by its own dialog state
  // (pullForwardRow) rather than lockDialogRow, so the two dialogs don't
  // fight over which row is open. also_set_active is always 1 here — Active
  // Estimating is always an in-play stage.
  const handlePullForward = useCallback(async ({ reason }) => {
    const row = pullForwardRow;
    if (!row) return;
    setSavingLock(row.ProjectID);
    try {
      await setEstimateSyncLock({
        project_id: row.ProjectID,
        locked: 1,
        reason,
        locked_by: userName,
        status: "Active Estimating",
        hubspot_status_at_lock: row.Status,
        also_set_active: 1,
      });
      setPullForwardRow(null);
      await load();
    } catch (e) {
      alert(`Pull forward failed: ${e.message}`);
    } finally {
      setSavingLock(null);
    }
  }, [pullForwardRow, userName, load]);

  // Explicitly triggered — batches a HubSpot call per ~100 currently-inactive
  // linked estimates, so it's not run automatically on every page load. See
  // hubspotSync.js's previewReactivations for what "would reactivate" means.
  const handlePreviewReactivations = useCallback(async () => {
    setReactivationPreview("loading");
    try {
      const linked = await sqlRead(`EXEC sp_GetHubSpotLinkedEstimates`);
      const inactive = (linked || []).filter(r => !r.IsActive && r.HubSpotDealID);
      const checked = await previewReactivations(inactive);
      setReactivationPreview(checked.filter(r => r.wouldReactivate));
    } catch (e) {
      alert(`Reactivation preview failed: ${e.message}`);
      setReactivationPreview(null);
    }
  }, []);

  async function saveCleanShieldOwners(owners) {
    setSavingConfig(true);
    try {
      const payload = JSON.stringify({ key: "cleanshield_owners", value: JSON.stringify(owners), updated_by: userName });
      await sqlRead(`EXEC sp_SaveDashboardConfig @payload='${sqlEscape(payload)}'`);
      setCleanShieldOwners(owners);
      setEditingFilter(false);
    } catch (e) {
      alert(`Failed to save filter: ${e.message}`);
    } finally {
      setSavingConfig(false);
    }
  }

  // ── Section grouping (applies division filter before grouping) ──────────────
  const { activeSales, activeConstr, activeUnassigned, sent, ytdTotal, cleanShieldHidden, salesDevelopment, realignment, proposalIssued } = useMemo(() => {
    const cleanShieldSet = new Set(cleanShieldOwners.map(n => n.toLowerCase()));
    const isCleanShield = r => cleanShieldSet.has((r.DealOwner || "").toLowerCase());

    const hiddenRows = showCleanShield ? [] : rows.filter(isCleanShield);
    const visibleRows = showCleanShield ? rows : rows.filter(r => !isCleanShield(r));

    // Pull the funnel-stage-specific rows out first so they show only in
    // their own section, not also in Sales/Construction/Unassigned/Sent —
    // "remaining" after this is Active Estimating rows only.
    const { salesDevelopment: salesDevRows, realignment: realignmentRows, proposalIssued: proposalIssuedRows, remaining: remainingRows } =
      bucketDashboardRows(visibleRows);

    const active = remainingRows.filter(r => !r.ProposalShippedDate);
    const shipped = remainingRows.filter(r => r.ProposalShippedDate);
    const sinceCutoff = sinceDate ? new Date(sinceDate) : null;

    return {
      activeSales:       active.filter(r => r.Source === "sales"),
      activeConstr:      active.filter(r => r.Source === "construction"),
      activeUnassigned:  active.filter(r => !r.Source),
      sent: shipped.filter(r => {
        if (!sinceCutoff) return true;
        return new Date(r.ProposalShippedDate) >= sinceCutoff;
      }),
      ytdTotal: shipped.reduce((s, r) => s + (Number(r.ActualShippedValue) || 0), 0),
      cleanShieldHidden: hiddenRows.length,
      salesDevelopment: salesDevRows,
      realignment: realignmentRows,
      proposalIssued: proposalIssuedRows,
    };
  }, [rows, sinceDate, cleanShieldOwners, showCleanShield]);

  // ── Legend ──────────────────────────────────────────────────────────────────
  const legendEntries = Object.entries({
    "Peter Tkachenko":    { bg: "#FBE2D5", text: "#222" },
    "Tommy Krystkiewicz": { bg: "#CAEDFB", text: "#222" },
    "Gauri Ambekar":      { bg: "#DAF2D0", text: "#222" },
    "Anthony Thomas":     { bg: "#C00000", text: "#fff" },
    "Ben Porter":         { bg: "#FFFF00", text: "#222" },
    "Unassigned":         { bg: "#D0D0D0", text: "#555" },
  });

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "var(--text2)" }}>
        Loading dashboard…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>
        Failed to load dashboard: {error}
      </div>
    );
  }

  const allActive = [...activeSales, ...activeConstr, ...activeUnassigned];

  return (
    <div style={{ padding: "16px 20px", maxWidth: 1400, margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>Estimating Dashboard</div>
          <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>
            Live estimates — click any cell to edit milestone data
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button onClick={load} style={{
            padding: "5px 12px", fontSize: 12, border: "1px solid var(--cs-border)",
            borderRadius: 4, background: "none", cursor: "pointer", color: "var(--text2)",
          }}>↺ Refresh</button>
        </div>
      </div>

      {/* ── Division filter banner — ALWAYS visible so users know what they're seeing ── */}
      {!showCleanShield ? (
        <div style={{
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderLeft: "5px solid #d97706",
          borderRadius: 4,
          padding: "10px 16px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
                FILTERED VIEW — Showing CleanSpace estimates only
              </div>
              <div style={{ fontSize: 12, color: "#78350f", marginTop: 2 }}>
                {cleanShieldHidden > 0
                  ? `${cleanShieldHidden} CleanShield deal${cleanShieldHidden !== 1 ? "s" : ""} are hidden (owned by: ${cleanShieldOwners.join(", ")})`
                  : cleanShieldOwners.length > 0
                    ? `No CleanShield deals in current pipeline (filter owners: ${cleanShieldOwners.join(", ")})`
                    : "No CleanShield owners configured — all deals are showing"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {isAdmin === true && (
              <button
                onClick={() => setEditingFilter(true)}
                style={{
                  padding: "4px 10px", fontSize: 11, border: "1px solid #d97706",
                  borderRadius: 3, background: "transparent", color: "#92400e", cursor: "pointer",
                }}
              >
                ⚙ Edit filter
              </button>
            )}
            <button
              onClick={() => setShowCleanShield(true)}
              style={{
                padding: "6px 16px", fontSize: 13, border: "1px solid #d97706",
                borderRadius: 4, background: "#d97706", color: "#fff", cursor: "pointer", fontWeight: 600,
              }}
            >
              Show all divisions →
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          background: "#eff6ff",
          border: "1px solid #93c5fd",
          borderLeft: "5px solid #3b82f6",
          borderRadius: 4,
          padding: "10px 16px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 16 }}>📊</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e40af" }}>
                Showing all divisions — CleanSpace + CleanShield
              </div>
              <div style={{ fontSize: 12, color: "#1e40af", marginTop: 2, opacity: 0.8 }}>
                {cleanShieldHidden === 0
                  ? `Includes all deal owners (CleanShield: ${cleanShieldOwners.join(", ")})`
                  : null}
                This view includes CleanShield deals. Switch back when done.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {isAdmin === true && (
              <button
                onClick={() => setEditingFilter(true)}
                style={{
                  padding: "4px 10px", fontSize: 11, border: "1px solid #93c5fd",
                  borderRadius: 3, background: "transparent", color: "#1e40af", cursor: "pointer",
                }}
              >
                ⚙ Edit CleanShield list
              </button>
            )}
            <button
              onClick={() => setShowCleanShield(false)}
              style={{
                padding: "6px 16px", fontSize: 13, border: "1px solid #1e40af",
                borderRadius: 4, background: "#1e40af", color: "#fff", cursor: "pointer", fontWeight: 600,
              }}
            >
              ← Filter to CleanSpace only
            </button>
          </div>
        </div>
      )}

      {/* ── Estimator legend ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
        {legendEntries.map(([name, colors]) => (
          <span key={name} style={{
            padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 500,
            background: colors.bg, color: colors.text,
            border: "1px solid rgba(0,0,0,0.12)",
          }}>
            {name}
          </span>
        ))}
        <span style={{ fontSize: 11, color: "var(--text3)", alignSelf: "center", marginLeft: 4 }}>
          ✎ = manually overridden date
        </span>
      </div>

      {/* ── HubSpot Overrides — visible, attributed log of every locked estimate ── */}
      {/* The accountability lever: the weekly digest only ever emails the       */}
      {/* "stale" bucket below — if that keeps growing, Sales isn't keeping     */}
      {/* HubSpot's stage current. Never written back to HubSpot.              */}
      {lockedEstimates.length > 0 && (() => {
        const OVERRIDE_BADGES = {
          resolved: { label: "✅ Resolved — safe to unlock", color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
          needs_review: { label: "🔄 Needs review", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
          stale: null, // default look — no badge needed, it's just "still waiting"
        };
        const withState = lockedEstimates.map(r => ({ ...r, _state: deriveOverrideState(r) }));
        const PRIORITY = { needs_review: 0, resolved: 1, stale: 2 };
        const sorted = [...withState].sort((a, b) => PRIORITY[a._state] - PRIORITY[b._state]);
        const actionableCount = withState.filter(r => r._state !== "stale").length;

        return (
          <div style={{
            background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 6,
            padding: "10px 16px", marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#7c2d12", marginBottom: 8 }}>
              🔒 HubSpot Overrides ({lockedEstimates.length})
              {actionableCount > 0 && (
                <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 8, color: "#b45309" }}>
                  {actionableCount} need{actionableCount === 1 ? "s" : ""} a look
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#7c2d12", opacity: 0.85, marginBottom: 10 }}>
              These estimates are frozen against the automatic HubSpot sync because HubSpot itself was stale for them — nothing here has been changed in HubSpot. Only the ones still genuinely waiting on Sales go in the weekly accountability email.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sorted.map(r => {
                const badge = OVERRIDE_BADGES[r._state];
                return (
                  <div key={r.ProjectID} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                    background: "#fff", border: `1px solid ${badge?.border || "#fed7aa"}`, borderRadius: 4, padding: "6px 10px", fontSize: 12,
                  }}>
                    <div>
                      <a href={`/estimates/${r.ProjectID}`} style={{ color: "inherit", fontWeight: 600, textDecoration: "none" }}>
                        {r.ProjectName}
                      </a>
                      {badge && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: badge.color, background: badge.bg, padding: "1px 6px", borderRadius: 999 }}>
                          {badge.label}
                        </span>
                      )}
                      <span style={{ color: "var(--text3)" }}> — {r.SyncLockReason}</span>
                      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                        Frozen at "{r.Status}" · {r.SyncLockedBy || "unknown"} · {fmtDate(r.SyncLockedAt) || "—"}
                        {r._state === "needs_review" && <> · HubSpot now shows "{r.LastSeenHubSpotStatus}"</>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      {r._state === "needs_review" && (
                        <button
                          onClick={() => setLockDialogRow({ ProjectID: r.ProjectID, ProjectName: r.ProjectName, liveLabel: r.LastSeenHubSpotStatus })}
                          title="Re-check this against what HubSpot shows now"
                          style={{
                            padding: "3px 10px", fontSize: 11, border: "1px solid #fdba74",
                            borderRadius: 3, background: "#fff7ed", color: "#7c2d12", cursor: "pointer",
                          }}
                        >
                          🔒 Re-lock
                        </button>
                      )}
                      <button
                        onClick={() => handleSetLock({ projectId: r.ProjectID, locked: false })}
                        disabled={savingLock === r.ProjectID}
                        title="Unlock — let the HubSpot sync manage this estimate again"
                        style={{
                          padding: "3px 10px", fontSize: 11, border: "1px solid var(--cs-border)",
                          borderRadius: 3, background: "none", cursor: "pointer", color: "var(--text2)",
                          opacity: savingLock === r.ProjectID ? 0.5 : 1,
                        }}
                      >
                        Unlock
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Reactivation preview — "if the sync ran now, what would come back?" ── */}
      <div style={{ marginBottom: 16 }}>
        <button
          onClick={handlePreviewReactivations}
          disabled={reactivationPreview === "loading"}
          style={{
            padding: "5px 12px", fontSize: 12, border: "1px solid var(--cs-border)",
            borderRadius: 4, background: "none", cursor: "pointer", color: "var(--text2)",
          }}
        >
          {reactivationPreview === "loading" ? "Checking HubSpot…" : "🔍 Preview reactivations"}
        </button>
        {Array.isArray(reactivationPreview) && (
          <div style={{
            marginTop: 8, background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 6, padding: "10px 16px",
          }}>
            {reactivationPreview.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text2)" }}>
                No currently-hidden estimates would come back — HubSpot agrees with everything hidden right now.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                  {reactivationPreview.length} hidden estimate{reactivationPreview.length !== 1 ? "s" : ""} would reactivate on the next sync — override anything that's actually stale in HubSpot:
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {reactivationPreview.map(r => (
                    <div key={r.ProjectID} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                      background: "#fff", border: "1px solid var(--cs-border)", borderRadius: 4, padding: "6px 10px", fontSize: 12,
                    }}>
                      <div>
                        <a href={`/estimates/${r.ProjectID}`} style={{ color: "inherit", fontWeight: 600, textDecoration: "none" }}>
                          {r.dealName || `Project ${r.ProjectID}`}
                        </a>
                        <span style={{ color: "var(--text3)" }}> — HubSpot now says "{r.liveLabel}"</span>
                      </div>
                      <button
                        onClick={() => setLockDialogRow(r)}
                        style={{
                          padding: "3px 10px", fontSize: 11, border: "1px solid #fdba74",
                          borderRadius: 3, background: "#fff7ed", color: "#7c2d12", cursor: "pointer", flexShrink: 0,
                        }}
                      >
                        🔒 Override
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {(() => {
        // The five movable blocks, keyed to match sectionOrder /
        // DEFAULT_SECTION_ORDER / SECTION_LABELS above.
        const sectionBlocks = {
          active: (
            <>
              <SectionTable
                title="Active Estimating — Sales"
                rows={activeSales}
                isSent={false}
                totalLabel="Total in House — Sales"
                onSave={handleSave}
                onHide={handleHide}
                onLock={row => setLockDialogRow(row)}
              />
              <SectionTable
                title="Active Estimating — Construction"
                rows={activeConstr}
                isSent={false}
                totalLabel="Total in House — Construction"
                onSave={handleSave}
                onHide={handleHide}
                onLock={row => setLockDialogRow(row)}
              />
              {activeUnassigned.length > 0 && (
                <SectionTable
                  title="Active Estimating — Source Not Set"
                  rows={activeUnassigned}
                  isSent={false}
                  totalLabel="Total in House — Unclassified"
                  onSave={handleSave}
                  onHide={handleHide}
                  onLock={row => setLockDialogRow(row)}
                />
              )}
            </>
          ),
          realignment: (
            // Proposal already went out; scope/commercial terms are being
            // revisited. Excluded from "Total in House" below.
            <SectionTable
              title="Post-Issuance Realignment"
              rows={realignment}
              isSent={false}
              totalLabel="Total — Realignment"
              onSave={handleSave}
              onHide={handleHide}
              onLock={row => setLockDialogRow(row)}
            />
          ),
          proposalIssued: (
            // Proposal Sent / Waiting on PO / Closed Won — the estimating
            // work is effectively done. Collapsed by default (see
            // DEFAULT_COLLAPSED_SECTIONS) and excluded from "Total in House"
            // below; not gone, just out of the way until wanted.
            <SectionTable
              title="Proposal Issued"
              rows={proposalIssued}
              isSent={false}
              totalLabel="Total — Proposal Issued"
              onSave={handleSave}
              onHide={handleHide}
              onLock={row => setLockDialogRow(row)}
            />
          ),
          salesDevelopment: (
            // Not yet active estimating — excluded from "Total in House" below.
            // onPullForward is the dedicated one-click path into Active
            // Estimating (2026-09-30); onLock stays available too for the
            // rarer case of correcting straight to some other stage.
            <SectionTable
              title="Future Pipeline"
              rows={salesDevelopment}
              isSent={false}
              totalLabel="Total — Future Pipeline"
              onSave={handleSave}
              onHide={handleHide}
              onLock={row => setLockDialogRow(row)}
              onPullForward={row => setPullForwardRow(row)}
            />
          ),
          sent: (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Sent Since:</label>
                <input
                  type="date"
                  value={sinceDate}
                  onChange={e => setSinceDate(e.target.value)}
                  style={{ padding: "3px 8px", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 4 }}
                />
                <button onClick={() => setSinceDate(sevenDaysAgo())}
                  style={{ fontSize: 11, padding: "3px 8px", border: "1px solid var(--cs-border)", borderRadius: 4, background: "none", cursor: "pointer", color: "var(--text2)" }}>
                  Last 7 days
                </button>
                <button onClick={() => setSinceDate("")}
                  style={{ fontSize: 11, padding: "3px 8px", border: "1px solid var(--cs-border)", borderRadius: 4, background: "none", cursor: "pointer", color: "var(--text2)" }}>
                  All time
                </button>
              </div>
              <SectionTable
                title={`Sent Since Last Update (${fmtDate(sinceDate) || "All"})`}
                rows={sent}
                isSent={true}
                onSave={handleSave}
              />
            </>
          ),
        };
        const sectionCounts = {
          active: activeSales.length + activeConstr.length + activeUnassigned.length,
          realignment: realignment.length,
          proposalIssued: proposalIssued.length,
          salesDevelopment: salesDevelopment.length,
          sent: sent.length,
        };

        return sectionOrder.map(key => {
          const isCollapsed = !!collapsedSections[key];
          return (
            <div
              key={key}
              onDragOver={e => { e.preventDefault(); if (dragOverSection !== key) setDragOverSection(key); }}
              onDragLeave={() => setDragOverSection(prev => (prev === key ? null : prev))}
              onDrop={() => { moveSectionBefore(draggedSection, key); setDraggedSection(null); setDragOverSection(null); }}
              style={{
                border: dragOverSection === key && draggedSection && draggedSection !== key
                  ? "2px dashed #3b82f6" : "2px solid transparent",
                borderRadius: 6, transition: "border-color 0.1s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <div
                  draggable
                  onDragStart={() => setDraggedSection(key)}
                  onDragEnd={() => { setDraggedSection(null); setDragOverSection(null); }}
                  title="Drag to reorder this section"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "2px 9px", fontSize: 11, color: "var(--text3)",
                    background: "#f1f5f9", borderRadius: 4, cursor: "grab", userSelect: "none",
                  }}
                >
                  <span style={{ fontSize: 13, lineHeight: 1 }}>⠿</span>
                  {SECTION_LABELS[key]}
                </div>
                <button
                  onClick={() => toggleSectionCollapsed(key)}
                  title={isCollapsed ? "Expand this section" : "Collapse this section"}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "2px 8px", fontSize: 11, color: "var(--text3)",
                    background: "none", border: "1px solid var(--cs-border)", borderRadius: 4, cursor: "pointer",
                  }}
                >
                  {isCollapsed ? "▸" : "▾"} {sectionCounts[key]}
                </button>
              </div>
              {!isCollapsed && sectionBlocks[key]}
            </div>
          );
        });
      })()}

      {sectionOrder.join(",") !== DEFAULT_SECTION_ORDER.join(",") && (
        <div style={{ marginBottom: 4 }}>
          <button
            onClick={() => {
              setSectionOrder(DEFAULT_SECTION_ORDER);
              try { localStorage.removeItem(SECTION_ORDER_STORAGE_KEY); } catch {}
            }}
            style={{
              fontSize: 11, padding: "3px 8px", border: "1px solid var(--cs-border)",
              borderRadius: 4, background: "none", cursor: "pointer", color: "var(--text3)",
            }}
          >
            ↺ Reset section order
          </button>
        </div>
      )}

      {/* ── YTD summary ── */}
      <div style={{
        background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 6,
        padding: "12px 16px", display: "flex", gap: 32, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--text2)", fontWeight: 500 }}>Total Proposals Sent YTD</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtCurrency(ytdTotal)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "var(--text2)", fontWeight: 500 }}>Active in House</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {allActive.length} estimate{allActive.length !== 1 ? "s" : ""}
            {!showCleanShield && cleanShieldHidden > 0 && (
              <span style={{ fontSize: 12, color: "#78350f", fontWeight: 400, marginLeft: 6 }}>
                (+{cleanShieldHidden} CleanShield hidden)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Mark as Shipped button row ── */}
      {allActive.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 6 }}>Mark a proposal shipped:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {allActive.map(r => (
              <button
                key={r.ProjectID}
                onClick={() => setShippingRow(r)}
                style={{
                  padding: "3px 10px", fontSize: 11, border: "1px solid var(--cs-border)",
                  borderRadius: 4, background: "none", cursor: "pointer", color: "var(--text2)",
                  ...(() => { const c = estimatorColor(r.AssignedEstimator); return { background: c.bg, color: c.text }; })(),
                }}
              >
                ✈ {r.ProjectName}
              </button>
            ))}
          </div>
        </div>
      )}

      {shippingRow && (
        <MarkShippedDialog
          row={shippingRow}
          onSave={handleSave}
          onClose={() => setShippingRow(null)}
        />
      )}

      {editingFilter && isAdmin === true && (
        <DivisionFilterEditor
          owners={cleanShieldOwners}
          onSave={saveCleanShieldOwners}
          onClose={() => setEditingFilter(false)}
          saving={savingConfig}
          allDealOwners={[...new Set(rows.map(r => r.DealOwner).filter(Boolean))].sort()}
        />
      )}

      {lockDialogRow && (
        <SyncLockDialog
          row={lockDialogRow}
          saving={savingLock === lockDialogRow.ProjectID}
          onClose={() => setLockDialogRow(null)}
          onSave={({ reason, status }) => handleSetLock({
            projectId: lockDialogRow.ProjectID,
            locked: true,
            reason,
            status,
            // What HubSpot actually showed at this moment — the "before"
            // snapshot deriveOverrideState compares future syncs against.
            hubspotStatusAtLock: lockDialogRow.liveLabel || lockDialogRow.Status,
          })}
        />
      )}

      {pullForwardRow && (
        <PullForwardDialog
          row={pullForwardRow}
          saving={savingLock === pullForwardRow.ProjectID}
          onClose={() => setPullForwardRow(null)}
          onSave={handlePullForward}
        />
      )}

      {saving && (
        <div style={{
          position: "fixed", bottom: 16, right: 20, background: "#1e293b", color: "#fff",
          padding: "6px 14px", borderRadius: 4, fontSize: 12, opacity: 0.9,
        }}>
          Saving…
        </div>
      )}
    </div>
  );
}
